/**
 * profile — per-user overlay store backed by `/api/v2/profile`.
 *
 * Holds the persistent `market_role`, applied-preset history, pulse-tile
 * selection, pinned tools, and the role-driven "suggested" entries that
 * later "Mijn omgeving" sections offer to install. State mirrors the
 * favorites.js pattern: a module-level ref + helper functions; callers
 * subscribe via `useProfile()`.
 */
import ProfileRepository from '@/repositories/ProfileRepository';
import { computed, ref } from 'vue';

const emptyOnboarding = () => ({
  status: 'pending',
  version: 1,
  answers: {
    organisation_type: null,
    role: null,
    interests: [],
    use_cases: [],
  },
  completed_at: null,
});

const emptyProfile = () => ({
  user_id: '',
  tenant_id: null,
  market_role: 'none',
  pulse_tile_selection: [],
  pinned_tools: [],
  suggested_charts: [],
  suggested_alert_templates: [],
  applied_presets: [],
  onboarding: emptyOnboarding(),
  last_seen_at: null,
  created_at: null,
  updated_at: null,
});

const profile = ref(emptyProfile());
const rolePresets = ref([]);
const products = ref([]);
const isLoaded = ref(false);

// The onboarding route guard and App.vue both load the profile at boot; share
// one in-flight request instead of hitting `/profile/me` twice.
let profileInFlight = null;

export function loadProfile() {
  if (profileInFlight) return profileInFlight;
  profileInFlight = (async () => {
    try {
      const response = await ProfileRepository.getProfile();
      if (response?.data) {
        profile.value = { ...emptyProfile(), ...response.data };
        isLoaded.value = true;
      }
      return profile.value;
    } catch (error) {
      return profile.value;
    }
  })().finally(() => {
    profileInFlight = null;
  });
  return profileInFlight;
}

export function isProfileLoaded() {
  return isLoaded.value;
}

export async function patchProfile(patch) {
  // Only send keys that are defined; the backend treats `model_dump(exclude_unset=True)`.
  const payload = {};
  if (patch.market_role !== undefined) payload.market_role = patch.market_role;
  if (patch.pulse_tile_selection !== undefined)
    payload.pulse_tile_selection = patch.pulse_tile_selection;
  if (patch.pinned_tools !== undefined) payload.pinned_tools = patch.pinned_tools;

  const response = await ProfileRepository.patchProfile(payload);
  if (response?.data) {
    profile.value = { ...emptyProfile(), ...response.data };
    isLoaded.value = true;
  }
  return profile.value;
}

export async function loadRolePresets() {
  try {
    const response = await ProfileRepository.getRolePresets();
    rolePresets.value = response?.data?.items || [];
  } catch (error) {
    rolePresets.value = [];
  }
  return rolePresets.value;
}

export async function applyPreset(role) {
  if (!role) return null;
  const response = await ProfileRepository.applyPreset(role);
  // The apply-preset response only carries the change-set; refresh the
  // profile so suggestions/pinned_tools/applied_presets are reflected.
  await loadProfile();
  return response?.data || null;
}

export async function loadProducts() {
  try {
    const response = await ProfileRepository.getProducts();
    products.value = response?.data?.items || [];
  } catch (error) {
    products.value = [];
  }
  return products.value;
}

export function needsOnboarding() {
  // The funnel is the single source of truth: a user is onboarded only once
  // their stored onboarding state leaves the `pending` status (via complete or
  // skip). Existing/pre-launch users are migrated to `completed` server-side.
  //
  // The empty in-memory profile also has a pending-shaped onboarding object so
  // components can render safely before the first API response. Do not treat
  // that placeholder as authoritative; otherwise the router can send returning
  // users into onboarding before `/profile/me` has loaded.
  return isLoaded.value && profile.value.onboarding?.status === 'pending';
}

/**
 * @deprecated Use `needsOnboarding()` instead. Kept as an alias for one release
 * while the RolePickerModal is retired; will be removed alongside it.
 */
export function isFirstTimeUser() {
  return needsOnboarding();
}

export function useProfile() {
  return {
    profile: computed(() => profile.value),
    rolePresets: computed(() => rolePresets.value),
    products: computed(() => products.value),
    isLoaded: computed(() => isLoaded.value),
    onboarding: computed(() => profile.value.onboarding || emptyOnboarding()),
    needsOnboarding: computed(() => needsOnboarding()),
    // Deprecated alias — mirrors `needsOnboarding` until RolePickerModal is gone.
    isFirstTimeUser: computed(() => isFirstTimeUser()),
    loadProfile,
    patchProfile,
    loadRolePresets,
    applyPreset,
    loadProducts,
  };
}

export default {
  profile,
  rolePresets,
  products,
  isLoaded,
  isProfileLoaded,
  loadProfile,
  patchProfile,
  loadRolePresets,
  applyPreset,
  loadProducts,
  needsOnboarding,
  isFirstTimeUser,
  useProfile,
};
